import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Globe, Users, MapPin, TrendingUp, Clock, MousePointer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6'];

export const Website = () => {
  const [websiteData, setWebsiteData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/analytics/website`);
        setWebsiteData(response.data);
      } catch (error) {
        console.error('Error fetching website data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading || !websiteData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="website-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Website Analytics</h1>
            <p className="text-slate-500 mt-1">Google Analytics data from GiriUK reports</p>
          </div>
          <Badge variant="outline" className="text-purple-600 border-purple-200">giriuk.com</Badge>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="website-kpis">
          {websiteData.kpis.map((kpi, index) => {
            const icons = [Users, Users, TrendingUp, MousePointer];
            const colors = ['purple', 'pink', 'emerald', 'blue'];
            const IconComponent = icons[index];
            return (
              <Card key={kpi.label} className="bg-white shadow-sm card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className={`p-2 bg-${colors[index]}-100 rounded-lg`}>
                      <IconComponent className={`h-5 w-5 text-${colors[index]}-600`} />
                    </div>
                    <Badge variant="secondary" className={kpi.changeType === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {kpi.change}
                    </Badge>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-slate-500">{kpi.label}</p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">{kpi.value}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Section */}
        <Tabs defaultValue="traffic" className="w-full">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="traffic">Traffic Sources</TabsTrigger>
            <TabsTrigger value="demographics">Demographics</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>

          <TabsContent value="traffic" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Traffic Source Chart */}
              <Card className="bg-white shadow-sm" data-testid="traffic-source-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">Traffic Sources Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={websiteData.trafficSources}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="users"
                          nameKey="source"
                          label={({ source, percentage }) => `${source.split(' ')[0]} ${percentage.toFixed(0)}%`}
                        >
                          {websiteData.trafficSources.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value.toLocaleString(), 'Users']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Traffic Source Table */}
              <Card className="bg-white shadow-sm" data-testid="traffic-source-table">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">Traffic Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {websiteData.trafficSources.map((source, index) => (
                      <div key={source.source} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                          <span className="font-medium text-slate-700">{source.source}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-600">{source.users.toLocaleString()} users</span>
                          <Badge variant="outline">{source.percentage.toFixed(1)}%</Badge>
                          <Badge className="bg-purple-100 text-purple-700">{source.engagement_rate.toFixed(1)}% eng</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="demographics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country Chart */}
              <Card className="bg-white shadow-sm" data-testid="demographics-chart">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-purple-500" />
                    Users by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={websiteData.demographics} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                        <YAxis dataKey="country" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} width={80} />
                        <Tooltip formatter={(value) => [value.toLocaleString(), 'Users']} />
                        <Bar dataKey="users" radius={[0, 4, 4, 0]}>
                          {websiteData.demographics.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Demographics Table */}
              <Card className="bg-white shadow-sm" data-testid="demographics-table">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">Country Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-medium text-slate-500">Country</th>
                          <th className="text-right p-2 text-sm font-medium text-slate-500">Users</th>
                          <th className="text-right p-2 text-sm font-medium text-slate-500">%</th>
                          <th className="text-right p-2 text-sm font-medium text-slate-500">Engaged</th>
                        </tr>
                      </thead>
                      <tbody>
                        {websiteData.demographics.map((country) => (
                          <tr key={country.country} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-medium text-slate-800">{country.country}</td>
                            <td className="p-2 text-right text-slate-600">{country.users.toLocaleString()}</td>
                            <td className="p-2 text-right text-slate-600">{country.percentage.toFixed(1)}%</td>
                            <td className="p-2 text-right text-slate-600">{country.engaged_sessions.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="trends" className="space-y-6">
            <Card className="bg-white shadow-sm" data-testid="trends-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800">Monthly User & Session Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={websiteData.trendData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                      <Legend />
                      <Bar dataKey="users" name="Active Users" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="sessions" name="Sessions" fill="#EC4899" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Key Insight */}
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg" data-testid="website-insight">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Globe className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Key Insight</h3>
                <p className="text-white/90 mt-2">
                  56% of your website traffic comes from India, followed by UK at 16.6%. Organic search drives 55% of all traffic 
                  with a 54% engagement rate. Consider creating more UK-focused content to improve local market penetration.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Website;
